import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PlatformService } from '@panamax/app-state';
import { TransactionRowText } from '@payments/models/payment-transaction';
import { TransactionRowDesktopConstants } from '@payments/payments-shared/transaction-row/desktop/transaction-row-desktop.component';
import { InvoiceIsrService } from '@payments/services/invoice-isr.service';
import { ModalWithNavComponent } from '@shared/components/modal-with-nav/modal-with-nav.component';
import { LoadingSpinnerComponent } from '@shared/components/popovers/loading-spinner/loading-spinner.component';
import { ModalService } from '@shared/services/modal/modal.service';
import { BankStoreService } from '@usf/ngrx-payment';
import { BankDetailsState } from '@usf/ngrx-payment/lib/models/state/bank-details.state';
import {
  CustomerReview,
  PaymentTransactionsViewModel,
} from '@usf/payment-types';
import { Observable, Subscription, filter, take } from 'rxjs';
import { AddBankAccountModalComponent } from '../add-bank-account-modal/add-bank-account-modal.component';

@Component({
  selector: 'app-customer-summary-section',
  templateUrl: './customer-summary-section.component.html',
  styleUrls: ['./customer-summary-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerSummarySectionComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() customers: CustomerReview[];
  @Input() isBanksEditable = false;
  @Input() isSubmitted = false;
  @Input() transactions: PaymentTransactionsViewModel[] = [];
  @Input() accordionOpenByDefault = false;
  @Input() isLoading = true;
  @Input() totalAmount: number;
  @Input() isPaymentPreferencesOn: boolean;
  @Input() isBankRoutingValidationOn: boolean;
  private _accordionValues: string[] = [];
  @Input() set accordionValues(values: any[]) {
    this._accordionValues = values.map(v => `${v}`);
  }
  @Input() showOrderNumber = false;
  get accordionValues(): string[] {
    return this._accordionValues;
  }

  transactionRowText: TransactionRowText;
  isMobile: boolean;
  isTouch$: Observable<boolean>;
  bankDetailsSub$: Subscription;
  showFullPO = false;
  columnSizes: Map<
    TransactionRowDesktopConstants.ColumnNames,
    string | number
  > = new Map();
  readonly ColumnNames = TransactionRowDesktopConstants.ColumnNames;

  constructor(
    readonly platformService: PlatformService,
    private translateService: TranslateService,
    private modalController: ModalController,
    private modalService: ModalService,
    private invoiceIsrService: InvoiceIsrService,
    private bankStoreService: BankStoreService,
  ) {
    this.isTouch$ = this.platformService.isTouchExperience$;
    this.isMobile =
      this.platformService.platformType ===
      this.platformService.platformEnum.mobile;
  }

  ngOnInit() {
    this.transactionRowText = {
      invoice: this.translateService.instant(
        'i18n.payments.transactionRow.invoice',
      ),
      custPo: this.translateService.instant(
        'i18n.payments.transactionRow.custPo',
      ),
      issueDate: this.translateService.instant(
        'i18n.payments.transactionRow.issueDate',
      ),
      issueDateCut: this.translateService.instant(
        'i18n.payments.transactionRow.issueDateCut',
      ),
      amount: this.translateService.instant(
        'i18n.payments.transactionRow.amount',
      ),
      dueDate: this.translateService.instant(
        'i18n.payments.transactionRow.dueDate',
      ),
      amountDue: this.translateService.instant(
        'i18n.payments.transactionRow.amountDue',
      ),
      dueDateCut: this.translateService.instant(
        'i18n.payments.transactionRow.dueDateCut',
      ),
      amountCut: this.translateService.instant(
        'i18n.payments.transactionRow.amountCut',
      ),
      amountDueCut: this.translateService.instant(
        'i18n.payments.transactionRow.amountDueCut',
      ),
      pay: this.translateService.instant('i18n.common.pay'),
      paymentAmount: this.translateService.instant(
        'i18n.payments.transactionRow.paymentAmount',
      ),
    };
    this.accordionValues = this.accordionOpenByDefault
      ? this.customers.map(customer => `${customer.customerNumber}`)
      : this.accordionValues;

    this.updateColumnSizes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['showOrderNumber']) {
      this.updateColumnSizes();
    }
  }

  ngOnDestroy() {
    this.bankDetailsSub$?.unsubscribe();
  }

  calcTotalAmountByCustomer(transactions: PaymentTransactionsViewModel[]) {
    return transactions.reduce(
      (acc, transaction) => acc + (transaction.paymentAmount || 0),
      0,
    );
  }

  async openAddBankAccountModal(event: any, customer: CustomerReview) {
    if (this.isSubmitted || !this.isBanksEditable) {
      return;
    }
    event?.stopPropagation();

    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      ModalWithNavComponent,
      {
        rootPage: AddBankAccountModalComponent,
        rootPageParams: {
          customerNumber: customer.customerNumber,
          bankAccounts: customer.customerBankAccounts,
          showSegments: this.isPaymentPreferencesOn,
          isPrimaryByDefault: true,
          currentBankAccount: customer.selectedBankAccount,
          isBankRoutingValidationOn: this.isBankRoutingValidationOn,
        },
      },
      'modal-desktop-size-lg',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    await modal.present();
    const {
      data: { isSaved, bankAccount },
    } = await modal.onDidDismiss();
    if (bankAccount) {
      if (isSaved) {
        this.loadingModal();
        this.bankStoreService.addCustomerBankAccount(bankAccount);
      } else {
        this.bankStoreService.setBanksToPay([
          {
            ...bankAccount,
            customerNumber: bankAccount.customerNumber,
          },
        ]);
      }
    }
  }

  async viewInvoice(transaction: PaymentTransactionsViewModel) {
    this.invoiceIsrService.viewInvoice(
      transaction.isr.docID.toString(),
      transaction.transactionNumber,
      transaction.secondaryTransactionNumber,
    );
  }

  async loadingModal() {
    const loadingSpinnerModal = await this.modalController.create({
      component: LoadingSpinnerComponent,
      backdropDismiss: false,
      cssClass: 'modal-loading-spinner',
      componentProps: {
        loadingSpinnerModalMessage: this.translateService.instant(
          'i18n.payments.reviewAndSubmit.saving',
        ),
      },
    });

    await loadingSpinnerModal?.present();
    (
      this.bankStoreService.getBanksDetails$() as unknown as Observable<BankDetailsState>
    )
      .pipe(
        filter(bankState => !bankState.loading),
        take(1),
      )
      .subscribe(_ => {
        loadingSpinnerModal?.dismiss();
      });
  }

  trackByIdx(i) {
    return i;
  }

  async openMoreOptions(optionObject: {
    event: Event;
    transaction: PaymentTransactionsViewModel;
  }) {
    this.invoiceIsrService.openInvoiceOptions(optionObject);
  }

  poNumberClicked() {
    this.showFullPO = !this.showFullPO;
    this.updateColumnSizes();
  }

  updateColumnSizes() {
    this.columnSizes = TransactionRowDesktopConstants.columnSizes(
      false,
      true,
      this.isSubmitted,
      this.showFullPO,
      this.showOrderNumber,
    );
  }
}
